import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import { formatBillingDate, formatBillingPeriod, plural, todayInTallinn } from "../format";
import { useBillingInvoices, useBillingMoney } from "../hooks";
import { resolveInvoiceDownloadUrl } from "../repository";
import type { BillingInvoice } from "../types";
import { RestrictedActionButton } from "./RestrictedActionButton";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface StatusPresentation {
  label: string;
  variant: BadgeProps["variant"];
  className?: string;
}

type InvoiceCopy = Translations["billing"]["invoices"];

/**
 * Statuses that settle to one badge whatever the date says.
 *
 * A Map keyed on the raw status, not an object: `status` is backend-supplied
 * text, and a plain object lookup would also reach inherited members.
 */
function fixedBadge(status: string, copy: InvoiceCopy): StatusPresentation | undefined {
  return new Map<string, StatusPresentation>([
    [
      "paid",
      {
        label: copy.statusPaid,
        variant: "secondary",
        className: "bg-success-100 text-success-800 hover:bg-success-100",
      },
    ],
    ["draft", { label: copy.statusDraft, variant: "secondary" }],
    ["void", { label: copy.statusVoid, variant: "outline" }],
    ["uncollectible", { label: copy.statusUncollectible, variant: "secondary" }],
    ["overdue", { label: copy.statusOverdue, variant: "destructive" }],
  ]).get(status);
}

/** An unpaid invoice, however the mirror spells it. */
const UNPAID_STATUSES = new Set(["open", "due"]);

/**
 * Present a Stripe invoice status as one of the brief's badges.
 *
 * Stripe's vocabulary is draft / open / paid / uncollectible / void — there is
 * no "overdue" status, so overdue is derived from an unpaid invoice whose due
 * date has passed, exactly as the read-only rule is derived server-side. An
 * unknown status renders as itself rather than blanking the row.
 */
function statusPresentation(invoice: BillingInvoice, copy: InvoiceCopy): StatusPresentation {
  if (UNPAID_STATUSES.has(invoice.status)) {
    // A declined automatic charge outranks the date. An invoice Stripe collects
    // itself carries no due date at all, so "Due"/"Overdue" would be derived
    // from a `due_on` the mirror never filled in — and the customer's next step
    // is a new payment method, not a bank transfer by a deadline.
    if (invoice.payment_failed_on) {
      return { label: copy.statusPaymentFailed, variant: "destructive" };
    }
    // No due date at all means Stripe collects this one itself and has not
    // failed yet — there is no deadline to be past, so it is simply outstanding.
    // Tallinn, not UTC: every billing date the backend produces is a Tallinn
    // calendar date, and a UTC comparison is a day behind until 03:00 EEST.
    return invoice.due_on !== null && invoice.due_on < todayInTallinn()
      ? { label: copy.statusOverdue, variant: "destructive" }
      : { label: copy.statusDue, variant: "outline" };
  }
  return fixedBadge(invoice.status, copy) ?? { label: invoice.status, variant: "outline" };
}

/** Newest first, falling back to the issue date for an invoice with no due date. */
function byDueDateDescending(left: BillingInvoice, right: BillingInvoice) {
  return (right.due_on ?? right.issued_on).localeCompare(left.due_on ?? left.issued_on);
}

/** Send the browser to the archived PDF, or to Stripe's hosted page. */
function openInvoice(invoice: BillingInvoice, url: string) {
  if (!invoice.pdf_storage_path) {
    // Stripe's hosted invoice page is a page, not a file — opening it in a tab
    // is the only sensible thing to do with it.
    window.open(url, "_blank", "noopener");
    return;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = `${invoice.invoice_number}.pdf`;
  // The anchor must be in the document — a detached click() is a no-op in
  // Firefox.
  document.body.append(link);
  link.click();
  link.remove();
}

interface InvoiceRowProps {
  invoice: BillingInvoice;
  copy: InvoiceCopy;
  money: (cents: number, currency?: string) => string;
  isDownloading: boolean;
  download: (invoice: BillingInvoice) => void;
}

function InvoiceRow({ invoice, copy, money, isDownloading, download }: InvoiceRowProps) {
  const presentation = statusPresentation(invoice, copy);
  const hasDocument = Boolean(invoice.pdf_storage_path || invoice.hosted_invoice_url);
  const downloadDisabledReason = !hasDocument
    ? copy.downloadUnavailable
    : isDownloading
      ? copy.downloadPending
      : undefined;

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell>
        <p className="font-medium">{invoice.invoice_number}</p>
        <p className="text-xs text-muted-foreground">
          {fill(plural(copy.buildingCount_one, copy.buildingCount_other, invoice.building_count), {
            count: String(invoice.building_count),
          })}
        </p>
      </TableCell>
      <TableCell>{formatBillingPeriod(invoice.period_start, invoice.period_end)}</TableCell>
      <TableCell>{invoice.due_on ? formatBillingDate(invoice.due_on) : copy.dueOnIssue}</TableCell>
      <TableCell>
        <Badge variant={presentation.variant} className={presentation.className}>
          {presentation.label}
        </Badge>
        {invoice.paid_on && (
          <p className="mt-1 text-xs text-muted-foreground">
            {fill(copy.paidOn, { date: formatBillingDate(invoice.paid_on) })}
          </p>
        )}
      </TableCell>
      <TableCell className="text-right font-medium">
        {money(invoice.amount_cents, invoice.currency)}
      </TableCell>
      <TableCell className="text-right">
        <RestrictedActionButton
          {...pageInfoTarget("billing.downloadInvoice")}
          variant="ghost"
          size="sm"
          disabledReason={downloadDisabledReason}
          onClick={() => download(invoice)}
          aria-label={fill(copy.downloadLabel, { number: invoice.invoice_number })}
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {copy.download}
        </RestrictedActionButton>
      </TableCell>
    </TableRow>
  );
}

export function BillingInvoicesPage() {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const query = useBillingInvoices();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const copy = t.billing.invoices;

  const download = async (invoice: BillingInvoice) => {
    setDownloadingId(invoice.id);
    try {
      const url = await resolveInvoiceDownloadUrl(invoice);
      if (url) openInvoice(invoice, url);
    } catch (error) {
      // Storage and RPC messages are English prose written for a log; the user
      // gets the localised failure and the console keeps the detail.
      console.error("Invoice download failed", error);
      toast.error(copy.downloadFailed);
    } finally {
      setDownloadingId(null);
    }
  };

  if (query.isLoading) return <div className="h-64 animate-pulse bg-muted" />;
  if (query.isError || !query.data) {
    return (
      <div className="border-y border-destructive/40 py-6">
        <p className="font-medium">{t.billing.errors.invoicesTitle}</p>
        <Button className="mt-4" variant="outline" onClick={() => query.refetch()}>
          {t.billing.errors.retry}
        </Button>
      </div>
    );
  }

  if (query.data.length === 0) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center text-center">
        <h2 className="font-semibold">{copy.emptyTitle}</h2>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">{copy.emptyBody}</p>
      </div>
    );
  }

  const invoices = [...query.data].sort(byDueDateDescending);

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="font-medium tracking-wider">{copy.invoice}</TableHead>
          <TableHead className="font-medium tracking-wider">{copy.period}</TableHead>
          <TableHead className="font-medium tracking-wider">{copy.due}</TableHead>
          <TableHead className="font-medium tracking-wider">{copy.status}</TableHead>
          <TableHead className="text-right font-medium tracking-wider">{copy.amount}</TableHead>
          <TableHead className="text-right font-medium tracking-wider">{copy.pdf}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <InvoiceRow
            key={invoice.id}
            invoice={invoice}
            copy={copy}
            money={money}
            isDownloading={downloadingId === invoice.id}
            download={download}
          />
        ))}
      </TableBody>
    </Table>
  );
}
